/**
 * Design tokens for the Schedule calendar (week / month / day views, the
 * session drawer and the page header in calendar mode). Values come straight
 * from the Schedule design spec so every piece of the view matches it; the
 * rest of the admin panel keeps the global MUI theme.
 */
export const SCHED = {
  font: '"DM Sans", system-ui, sans-serif',
  primary: '#D23650',
  primaryHover: '#B82A43',
  primaryText: '#C22F48',
  danger: '#B02A43',
  ink: '#2B2B2E',
  inkSoft: '#3A3A3F',
  muted: '#66666D',
  legend: '#55555C',
  canvas: '#FBF8F8',
  border: '#EFE9EA',
  control: '#E4DDDF',
  hover: '#F6F2F3',
  track: '#F1ECED',
  rowLine: '#F4F0F1',
  todayBorder: '#F3D3DA',
  todayTint: '#FDEDF0',
  cancelledTint: '#F1ECED',
  successBg: '#E6F4EC',
  successText: '#1F6B45',
  success: '#1F7A4D',
  waitBg: '#FFF1DC',
  waitText: '#7A4A00',
} as const;

/** `.btn` — 44px, 12px radius, white with a hairline border. */
export const btnSx = {
  height: 44,
  px: '16px',
  borderRadius: '12px',
  border: `1px solid ${SCHED.control}`,
  backgroundColor: '#FFFFFF',
  color: SCHED.ink,
  fontFamily: SCHED.font,
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
  boxShadow: 'none',
  gap: '8px',
  minWidth: 0,
  '&:hover': { backgroundColor: SCHED.hover, borderColor: SCHED.control, boxShadow: 'none' },
  '& .MuiButton-startIcon': { mr: 0, ml: 0 },
};

/** `.btn.p` — the filled primary button. */
export const btnPrimarySx = {
  ...btnSx,
  backgroundColor: SCHED.primary,
  borderColor: SCHED.primary,
  color: '#FFFFFF',
  '&:hover': { backgroundColor: SCHED.primaryHover, borderColor: SCHED.primaryHover, boxShadow: 'none' },
};

/** `.btn.sm` */
export const btnSmSx = { height: 36, px: '12px', fontSize: '13px', borderRadius: '10px' };

/** `.btn.gh.red` — borderless destructive text button. */
export const btnDangerGhostSx = {
  ...btnSx,
  borderColor: 'transparent',
  backgroundColor: 'transparent',
  color: SCHED.danger,
  '&:hover': { backgroundColor: SCHED.hover, borderColor: 'transparent', boxShadow: 'none' },
};

/** `.seg` — grey track holding pill buttons. */
export const segSx = {
  display: 'flex',
  gap: '4px',
  p: '4px',
  backgroundColor: SCHED.track,
  borderRadius: '14px',
};

/** `.seg button` / `.seg button.on` */
export const segBtnSx = (active: boolean) => ({
  height: 36,
  px: '16px',
  border: 0,
  borderRadius: '10px',
  cursor: 'pointer',
  fontFamily: SCHED.font,
  fontSize: '14px',
  fontWeight: active ? 700 : 500,
  whiteSpace: 'nowrap' as const,
  color: active ? SCHED.ink : SCHED.inkSoft,
  backgroundColor: active ? '#FFFFFF' : 'transparent',
  boxShadow: active ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  '&:focus-visible': { outline: `2px solid ${SCHED.ink}`, outlineOffset: 1 },
});

/** `.ib` — round icon button. */
export const iconBtnSx = (size = 44) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  border: `1px solid ${SCHED.control}`,
  backgroundColor: '#FFFFFF',
  color: SCHED.ink,
  p: 0,
  flexShrink: 0,
  '&:hover': { backgroundColor: SCHED.hover },
});

// Member avatar washes from the design, picked per member so a row keeps its colour.
const AVATAR_TINTS = ['#F3E6E0', '#E3ECF3', '#E8EFE3', '#F6EDD9', '#EEE9FB', '#FDE8EC', '#E4DEDF'];
export const avatarTint = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_TINTS[h % AVATAR_TINTS.length];
};
